import React from 'react';
import { Responsive, WidthProvider, Layout, Layouts } from 'react-grid-layout';
import { Box } from '@mui/material';
import { PlacedWidget, LayoutItem } from './types';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

/**
 * Breakpoints measure the GRID CONTAINER, not the window — react-grid-layout's
 * WidthProvider reports the element width. With a 280px sidebar a 1440px
 * laptop gives roughly 1100px of grid, which fell under the old lg:1280 and
 * dropped to a 10-column layout. Four-wide tiles then packed 2 per row and
 * left 2 columns permanently empty, which is what made the board look sparse
 * and half-used.
 *
 * 12 columns is held all the way down to 960px so the intended 3-up rows
 * survive on any normal laptop. Below that the tiles would be under ~280px
 * wide and genuinely unreadable, so it steps down rather than squeezing.
 */
const BREAKPOINTS = { lg: 1100, md: 960, sm: 720, xs: 480, xxs: 0 };
const COLS = { lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 };

/** Breakpoints whose column count matches the stored layout's 12-col basis. */
const PERSISTABLE = new Set(['lg', 'md']);

interface WidgetGridProps {
  widgets: PlacedWidget[];
  isEditing: boolean;
  compact?: boolean;
  onLayoutChange: (widgets: PlacedWidget[]) => void;
  renderWidget: (widget: PlacedWidget) => React.ReactNode;
}

/**
 * Responsive widget grid.
 *
 * Layout is only persisted from the `lg` breakpoint. Below it react-grid-layout
 * derives a stacked arrangement, and saving that would overwrite the user's
 * desktop board the first time they opened the dashboard on a laptop — a
 * genuinely infuriating bug to diagnose because it looks like the layout
 * "randomly reset".
 */
const WidgetGrid: React.FC<WidgetGridProps> = ({
  widgets,
  isEditing,
  compact = false,
  onLayoutChange,
  renderWidget,
}) => {
  const [breakpoint, setBreakpoint] = React.useState<string>('lg');

  const layouts: Layouts = React.useMemo(() => {
    const lg: LayoutItem[] = widgets.map((w) => ({
      i: String(w.id),
      x: w.gridX,
      y: w.gridY,
      w: w.gridW,
      h: w.gridH,
      minW: w.descriptor?.minSize?.w ?? 2,
      minH: w.descriptor?.minSize?.h ?? 2,
      maxW: w.descriptor?.maxSize?.w ?? 12,
      maxH: w.descriptor?.maxSize?.h ?? 12,
      // Locked entirely outside edit mode so a stray drag on a chart cannot
      // silently rearrange someone's dashboard.
      static: !isEditing,
    }));
    return { lg };
  }, [widgets, isEditing]);

  const handleLayoutChange = (current: Layout[]) => {
    // Only persist from a 12-column breakpoint. Below that the grid derives a
    // narrower arrangement, and saving it would silently overwrite the user's
    // desktop board the first time they opened the dashboard on a small
    // window — a change that looks like the layout "randomly reset".
    if (!isEditing || !PERSISTABLE.has(breakpoint)) return;

    const byId = new Map(current.map((l) => [l.i, l]));
    const next = widgets.map((w) => {
      const l = byId.get(String(w.id));
      if (!l) return w;
      if (l.x === w.gridX && l.y === w.gridY && l.w === w.gridW && l.h === w.gridH) return w;
      return { ...w, gridX: l.x, gridY: l.y, gridW: l.w, gridH: l.h };
    });

    // Only propagate when geometry actually moved. react-grid-layout fires
    // this on mount and on every breakpoint change, which would otherwise
    // mark the board dirty the moment it renders.
    const moved = next.some((w, i) => w !== widgets[i]);
    if (moved) onLayoutChange(next);
  };

  return (
    <Box
      sx={{
        // Placeholder shown under a dragged tile.
        '& .react-grid-item.react-grid-placeholder': {
          backgroundColor: 'primary.main',
          opacity: 0.18,
          borderRadius: 2,
        },
        '& .react-grid-item': {
          transition: 'transform .18s ease, width .18s ease, height .18s ease',
        },
        '& .react-grid-item.react-draggable-dragging': {
          transition: 'none',
          zIndex: 3,
          cursor: 'grabbing',
        },
        '& .react-resizable-handle': {
          opacity: isEditing ? 0.5 : 0,
          pointerEvents: isEditing ? 'auto' : 'none',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& .react-grid-item': { transition: 'none' },
        },
      }}
    >
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={BREAKPOINTS}
        cols={COLS}
        // 68 left stat tiles floating in ~320px of white space at h=4. 56 puts
        // a 4-row tile at ~272px, which fits a header plus a headline figure
        // and a short list without padding it out.
        rowHeight={compact ? 46 : 56}
        margin={compact ? [10, 10] : [14, 14]}
        containerPadding={[0, 0]}
        isDraggable={isEditing}
        isResizable={isEditing}
        // Only the header grip starts a drag, so interactive widget content
        // (chart tooltips, links, buttons) still works in edit mode.
        draggableHandle=".widget-drag-handle"
        onLayoutChange={handleLayoutChange}
        onBreakpointChange={setBreakpoint}
        // Pull tiles upward into any gap. Without this a removed or resized
        // widget leaves a hole and the board drifts apart over time — the
        // single biggest reason a widget dashboard starts looking scattered.
        compactType="vertical"
        // Tiles may not overlap; the grid pushes rather than stacks.
        preventCollision={false}
        measureBeforeMount={false}
        useCSSTransforms
      >
        {widgets.map((w) => (
          <div key={String(w.id)}>{renderWidget(w)}</div>
        ))}
      </ResponsiveGridLayout>
    </Box>
  );
};

export default WidgetGrid;
